import React, { useContext, useState } from "react";
import Carousel from "react-multi-carousel";
import useFetch from "../hooks/useFetch";
import Heading from "../components/Heading";
import PageBanner from "../components/PageBanner";
import parse from "html-react-parser";
import { CgChevronDoubleRight, CgChevronDoubleLeft } from "react-icons/cg";
import { useParams } from "react-router-dom";
import HireBtn from "../components/HireBtn";
import ProjectDetailSkeleton from "../skeletons/ProjectDetailSkeleton";
import SideBar from "../components/SideBar";
import VideoModal from "../components/VideoModal";
import useTypeWriter from "../hooks/useTypeWriter";
import { AnimatePresence, motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import AnimationContext from "../contexts/animation/AnimationContext";

const ProjectDetail = () => {
  const { str } = useTypeWriter("Project Details", 250);
  const [videoModal, setVideoModal] = useState({ isOpen: false });
  const { ref, inView } = useInView({ threshold: 0.2 });
  const { setAnimation } = useContext(AnimationContext);
  const inViewVariants = setAnimation(inView);

  const { slug } = useParams();
  const { data: projectData, isLoading } = useFetch(
    `${process.env.REACT_APP_BACKEND_URL}/api/projects/?filters[slug]=${slug}&populate=imgs`
  );
  let project = {};
  project = projectData[0];
  const responsive = {
    screen: {
      breakpoint: { max: 6000, min: 0 },
      items: 1,
    },
    sm: {
      breakpoint: { max: 600, min: 0 },
      items: 1,
    },
  };

  return (
    <>
      <PageBanner
        id="project-details-banner"
        pageTitle={str}
        link={"Home"}
        linkTarget="/"
      />
      <section className="bg-scColor" id="project-details">
        <div className="container py-[40px] sm:py-[60px] md:py-[90px]">
          {!isLoading && (
            <>
              <Carousel
                responsive={responsive}
                infinite={false}
                keyBoardControl={true}
                customTransition={"transform 800ms ease-in-out"}
                transitionDuration={800}
                autoPlaySpeed={5000}
                autoPlay={true}
                customRightArrow={<CustomRightArrow />}
                customLeftArrow={<CustomLeftArrow />}
                showDots={true}
                dotListClass="bg-prColor/60"
                removeArrowOnDeviceType={"sm"}
                minimumTouchDrag="20"
              >
                {project.attributes.imgs.data.map((img, index) => (
                  <img
                    src={`${img.attributes.url}`}
                    alt="project-img"
                    key={index}
                    className="w-full h-[400px] max-h-[60vh] sm:max-h-[600px] sm:h-[450px] md:h-[500px] lg:h-[600px] sm:py-6  bg-prColor object-cover object-top hover:object-bottom transition-all duration-[7000ms] ease-linear overflow-hidden"
                  />
                ))}
              </Carousel>
              <motion.div
                ref={ref}
                variants={inViewVariants}
                initial="hidden"
                animate="visible"
                className="flex flex-col lg:flex-row justify-between mt-8 sm:mt-10 md:mt-16"
              >
                <main className="lg:w-[70%] flex-grow">
                  <div>
                    <Heading
                      title={project.attributes.title}
                      subTitle={project.attributes.shortTitle}
                    />
                  </div>

                  <div className="mt-12 sm:mt-16 md:mt-20 prose prose-headings:text-light prose-headings:text-xl sm:prose-headings:text-2xl prose-headings:font-['Poppins'] prose-p:text-base prose-p:sm:text-xl prose-ul:text-base prose-ul:sm:text-xl prose-p:text-dark prose-ul:text-dark  prose-strong:text-light text-light prose-a:text-light prose-li:list-outside">
                    {parse(project.attributes.desc)}
                  </div>
                </main>
                <div className="mt-12 lg:mt-0 lg:px-6 lg:w-fit">
                  {
                    <SideBar
                      title={"Information"}
                      infos={[
                        {
                          title: "Project Name:",
                          details: `${project.attributes.name}`,
                        },
                        {
                          title: "Client:",
                          details: `${project.attributes.client}`,
                        },
                      ]}
                      subTitle="Let's see"
                      links={[
                        {
                          url: `${
                            project.attributes.liveUrl || "javascript:void(0)"
                          }`,
                          icon: `${
                            project.attributes.liveUrl ? "live" : "video"
                          }`,
                        },
                        {
                          url: `${project.attributes.githubUrl}`,
                          icon: "github",
                        },
                      ]}
                      bgPr={true}
                      setVideoModal={setVideoModal}
                    />
                  }
                </div>
              </motion.div>
            </>
          )}
          {isLoading && <ProjectDetailSkeleton />}
        </div>
        <AnimatePresence exitBeforeEnter>
          {videoModal.isOpen && (
            <VideoModal
              url={project.attributes.videoUrl}
              setVideoModal={setVideoModal}
            />
          )}
        </AnimatePresence>
      </section>
      <HireBtn />
    </>
  );
};
const CustomRightArrow = ({ onClick, ...rest }) => {
  return (
    <button
      type="button"
      className="btn-secondary translate-in translate-x-60"
      style={{ right: "calc(4% - 1px)" }}
      onClick={() => onClick()}
    >
      <CgChevronDoubleRight fontSize={22} />
    </button>
  );
};
const CustomLeftArrow = ({ onClick, ...rest }) => {
  return (
    <button
      type="button"
      className="btn-secondary translate-in -translate-x-60"
      style={{ left: "calc(4% - 1px)" }}
      onClick={() => onClick()}
    >
      <CgChevronDoubleLeft fontSize={22} />
    </button>
  );
};
export default ProjectDetail;
